/* eslint-disable default-case */
/* eslint-disable no-case-declarations */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/naming-convention */
import { useContext } from "react";
import { useLogin, useLogout } from "react-admin";

import http from "../../auth/http-common";
import { URL } from "../../constants/api-constants";
import { AuthContext } from "../../contexts/auth";
import { cleanUserTokensFromLocalStorage, setUserTokensToLocalStorage } from "../../utils/user";
import { AUTH_FLOW } from "../models/auth";
import { NotifyFunction } from "../../types/notification";
import CognitoToken from "../../types/cognito-token";

const useAuthMutations = (notify?: NotifyFunction) => {
  const { authState, setAuthState } = useContext(AuthContext);
  const login = useLogin();
  const logout = useLogout();

  const handleAuthFlow = (data: any, password_digest: string, needChangeFlow: boolean) => {
    const { authFlow } = data;

    switch (authFlow) {
      case AUTH_FLOW.WITH_INVITATION_CODE: {
        setAuthState((oldData) => ({ ...oldData, password_digest, authFlow }));
        break;
      }
      case AUTH_FLOW.FIRST_SIGN_IN: {
        setAuthState((oldData) => ({ ...oldData, password_digest, authFlow }));
        break;
      }
      case AUTH_FLOW.MFA_SETUP: {
        const { authFlow: flowMfaSerUp, ...mfaSetupData } = data;

        setAuthState((oldData) => ({ ...oldData, password_digest, authFlow: flowMfaSerUp, mfaSetupData }));
        break;
      }
      case AUTH_FLOW.MFA_CHECK: {
        const { authFlow: flowMfaCheck, ...mfaCheckData } = data;

        if (needChangeFlow) {
          setAuthState((oldData) => ({ ...oldData, password_digest, authFlow: flowMfaCheck }));
        }

        setAuthState((oldData) => ({ ...oldData, password_digest, mfaCheckData }));
        break;
      }
      default: {
        console.warn("Unknown auth flow:", authFlow);
      }
    }
  };

  const completeAuthentication = (tokens: CognitoToken) => {
    const { access_token, refresh_token, session_token } = tokens;

    setAuthState((oldData) => ({
      ...oldData,
      mfaCheckData: null,
      mfaSetupData: null,
      authFlow: AUTH_FLOW.CHECK_EMAIL,
      isAuthLoading: false,
      accessToken: access_token,
      refreshToken: refresh_token,
      sessionToken: session_token,
      isAuthenticated: true,
      email: null,
      password_digest: null,
    }));
    setUserTokensToLocalStorage(access_token, refresh_token, session_token);
  };

  const checkAuthFlow = async ({ email }: { email: string }) => {
    try {
      const result = await http.post(URL.CHECK_AUTH_FLOW, {
        email,
      });

      const { authFlow } = result.data;
      setAuthState((oldData) => ({ ...oldData, authFlow, email }));
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  const redeemInvitationCode = async ({ email, code }: { code: string; email: string }) => {
    try {
      const result = await http.post(URL.REDEEM_INVITATION_CODE, {
        email: authState?.email || email,
        code,
      });

      setAuthState((oldData) => ({
        ...oldData,
        authFlow: result.data.authFlow,
      }));
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  const signIn = async ({
    email,
    password_digest,
    needChangeFlow = true,
  }: {
    email: string;
    password_digest: string;
    needChangeFlow?: boolean;
  }) => {
    try {
      const response = await login({
        email: authState?.email || email,
        password_digest,
      });

      const { data } = response;

      if (data?.authFlow) {
        handleAuthFlow(data, password_digest, needChangeFlow);
        return;
      }

      if (data?.access_token) {
        const tokens: CognitoToken = data;
        completeAuthentication(tokens);
        return;
      }

      throw new Error("Authentication failed");
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  const setPermanentPassword = async ({ email, password_digest }: { email: string; password_digest: string }) => {
    try {
      await http.post(URL.SET_PASSWORD, {
        email: authState?.email || email,
        password_digest,
      });

      await signIn({
        email,
        password_digest,
      });
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  const verifyMfa = async ({
    code,
    email,
    password_digest,
  }: {
    email: string;
    password_digest: string;
    code: string;
  }) => {
    try {
      await http.post(URL.VERIFY_MFA_TOKEN, {
        session: authState.mfaSetupData?.session,
        email: authState?.email || email,
        code,
      });

      await signIn({ email, password_digest, needChangeFlow: false });
      setAuthState((oldData) => ({
        ...oldData,
        authFlow: AUTH_FLOW.MFA_SETUP_COMPLETED,
      }));
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  const checkMfa = async ({ code, email }: { code: string; email: string }) => {
    try {
      // With MFA enabled, configure login under the "mfh-check" URL to redirect users to the home page after request.
      const response = await login({
        session: authState.mfaCheckData?.session,
        email: authState?.email || email,
        code,
      });

      const { data } = response;

      if (data?.access_token) {
        const tokens: CognitoToken = data;
        completeAuthentication(tokens);
        return;
      }

      throw new Error("Authentication failed");
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  const logOutUser = () => {
    setAuthState((oldData) => ({
      ...oldData,
      mfaCheckData: null,
      mfaSetupData: null,
      authFlow: AUTH_FLOW.CHECK_EMAIL,
      isAuthLoading: false,
    }));

    cleanUserTokensFromLocalStorage();
  };

  const signOut = async () => {
    try {
      await logout();

      logOutUser();
    } catch (error: any) {
      if (notify) {
        notify(error.message, { type: "error" });
      }
    }
  };

  return {
    checkAuthFlow,
    signIn,
    setPermanentPassword,
    authState,
    signOut,
    redeemInvitationCode,
    verifyMfa,
    checkMfa,
  };
};

export default useAuthMutations;
